import {
    ChangeDetectionStrategy,
    Component, computed,
    OnInit,
    signal,
    viewChild
} from '@angular/core';
import {MatButton, MatIconButton} from "@angular/material/button";
import {DomainService, FlagWithCount, FlagDomainsResponse, FlagDomainInfo} from "../../services/domain.service";
import {BusyService} from "../../services/busy.service";
import {finalize} from "rxjs/operators";
import {AlertService} from "../../services/alert.service";
import {UtilService} from "../../services/util.service";
import {MatFormField, MatLabel} from "@angular/material/form-field";
import {MatOption} from "@angular/material/autocomplete";
import {MatSelect} from "@angular/material/select";
import {FormsModule} from "@angular/forms";
import {MatPaginator, PageEvent} from "@angular/material/paginator";
import {
    MatCell,
    MatCellDef,
    MatColumnDef,
    MatHeaderCell, MatHeaderCellDef,
    MatHeaderRow, MatHeaderRowDef, MatRow, MatRowDef,
    MatTable,
    MatTableDataSource
} from "@angular/material/table";

import {AuthService} from "../../services/auth.service";
import {MatIcon} from "@angular/material/icon";
import {MatDialog} from "@angular/material/dialog";
import {CreateFlagDialogComponent} from "../../dialogs/create-flag-dialog/create-flag-dialog.component";
import {EditFlagDialogComponent} from "../../dialogs/edit-flag-dialog/edit-flag-dialog.component";
import {MatTooltip} from "@angular/material/tooltip";
import {Constants} from "../../util/constants";

@Component({
    imports: [
        MatButton,
        FormsModule,
        MatPaginator,
        MatCell,
        MatCellDef,
        MatColumnDef,
        MatHeaderCell,
        MatTable,
        MatHeaderRow,
        MatHeaderRowDef,
        MatRow,
        MatRowDef,
        MatHeaderCellDef,
        MatIcon,
        MatIconButton,
        MatTooltip
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
    templateUrl: './flags.component.html',
    styleUrl: './flags.component.scss'
})
export class FlagsComponent implements OnInit {
    // Flags with counts
    flags = signal<FlagWithCount[]>([]);
    
    // Only show sections that can make changes if user is admin or super admin
    isAdminPlus = computed(() => this.authService.isAdmin() || this.authService.isSuperAdmin());

    // Flags table columns
    readonly flagColumnsToDisplay = ['name', 'preventsDelete', 'domainCount', 'actions']

    // Domains table columns
    readonly domainColumnsToDisplay = ['domain', 'assigned'];

    readonly PAGE_SIZE_OPTIONS = [5, 10, 20];
    readonly DEFAULT_PAGE_SIZE = this.PAGE_SIZE_OPTIONS[1];

    // Flag domains table
    selectedFlag = signal<string>('');
    paginator = viewChild<MatPaginator>('paginator');
    flagDomains = signal<FlagDomainsResponse|undefined>(undefined);
    flagDomainsDataSource = computed(() =>
        new MatTableDataSource<FlagDomainInfo>(this.flagDomains()? this.flagDomains()!.domains : [])
    );
    totalDomains = computed(() => this.flagDomains()? this.flagDomains()!.totalCount : 0);
    currentPage = signal(0);
    pageSize = signal(this.DEFAULT_PAGE_SIZE);

    isBusy = computed(() => this.busyService.isBusy());

    constructor(
        private domainService: DomainService,
        private busyService: BusyService,
        private alertService: AlertService,
        private authService: AuthService,
        private dialog: MatDialog,
        private utilService: UtilService) {

        // This should be done with linkedSignal (once we update to Angular 20+)
        // effect(() => {
        //     this.updatedFlagNewName.set(this.flagToUpdate()?.name || '');
        // }, { allowSignalWrites: true });
    }
    
    ngOnInit() {
        this.loadFlags();
    }

    formatTimestamp(sd: string): string {
        return sd? Constants.US_TIMESTAMP_FORMAT.format(new Date(sd)) : "";
    }

    loadFlags() {
        this.busyService.showBusy();
        this.domainService.getFlagsWithCounts()
            .pipe(finalize(() => this.busyService.showNotBusy()))
            .subscribe({
                next: (flags) => {
                    this.flags.set(flags);
                },
                error: this.utilService.getErrorHandler("Error loading flags")
            });
    }
    
    addFlag() {
        const flagNames = this.flags().map((flag) => flag.name);
        this.dialog.open(CreateFlagDialogComponent, {
            disableClose: true,
            data: flagNames
        }).afterClosed().subscribe({
            next: (added) => {
                if (added)
                    this.loadFlags();
            }
        });
    }

    modifyFlag(flag: FlagWithCount) {
        const flagNames = this.flags().map((f) => f.name);
        this.dialog.open(EditFlagDialogComponent, {
            disableClose: true,
            data: {
                flagName: flag.name,
                preventsDelete: flag.preventsDelete,
                allFlags: flagNames
            }
        }).afterClosed().subscribe({
            next: (updated) => {
                if (updated)
                    this.loadFlags();
            }
        });
    }

    maybeDeleteFlag(flag: FlagWithCount) {
        let forceDelete = false;
        let msg;
        if (flag.domainCount == 0) {
            msg = `Are you sure you want to remove the <b>${flag.name}</b> flag?<br/>` +
                  "This flag is not currently in use by any domains, so it's safe to delete.";
        } else {
            msg = `Are you sure you want to remove the <b>${flag.name}</b> flag?<br/>` +
                `This flag is currently in use by ${flag.domainCount} domains, <br/>so please make sure you want to delete it!`;
            forceDelete = true;
        }
        this.alertService.confirm(msg).subscribe({
            next: (confirmed) => {
                if (confirmed) {
                    // Delete the flag (and any links to it)
                    this.busyService.showBusy();
                    this.domainService.deleteFlag(flag.name, forceDelete).pipe(
                        finalize(() => this.busyService.showNotBusy())
                    ).subscribe({
                        next: () => {
                            this.alertService.info(`Flag <b>${flag.name}</b> deleted successfully`);
                            this.loadFlags();
                        },
                        error: this.utilService.getErrorHandler("Error deleting flag")
                    });
                }
            }
        })

    }
    
    loadDomains(flagName: string, clean: boolean = false) {
        if (clean) {
            this.currentPage.set(0);
            this.pageSize.set(this.DEFAULT_PAGE_SIZE);
            if (flagName == this.selectedFlag()) {
                this.selectedFlag.set('');
                this.flagDomains.set(undefined);
                return;
            }
        }
        this.selectedFlag.set(flagName);
        this.busyService.showBusy();
        this.domainService.getDomainsByFlag(flagName, this.currentPage(), this.pageSize())
            .pipe(finalize(() => this.busyService.showNotBusy()))
            .subscribe({
                next: (response) => {
                    this.flagDomains.set(response);
                },
                error: this.utilService.getErrorHandler(`Error loading domains for ${flagName} flag`),
            });
    }
    
    onPageChange(event: PageEvent) {
        this.currentPage.set(event.pageIndex);
        this.pageSize.set(event.pageSize);
        this.loadDomains(this.selectedFlag());
    }
}
